import React, { useEffect, useState, useRef, useContext } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { updateProfile } from "../api";
import readStreamAsBase64 from "../utils/readStreamAsBase64";
import { AddPhotoAlternate } from "@mui/icons-material";
import UserContext from "../contexts/UserContext";
import resizeFile from "../utils/resizeFile";
import Socials from "./Auth/Socials";
import config from "../config";

export default function DJProfileModal({ isInitialDJSignup, handleClose }) {
  const [state, setState] = useState({});
  const [errors, setErrors] = useState(undefined);
  const [avatarFile, setAvatarFile] = useState("");
  const [socials, setSocials] = useState({});

  const userContext = useContext(UserContext);

  const handleChangeAvatar = async (e) => {
    if (e) {
      const avatar = await resizeFile(e.target.files[0]);
      setState((s) => ({
        ...s,
        avatar,
        avatarHasChanged: true,
      }));

      const avatarObjectURL = URL.createObjectURL(e.target.files[0]);
      setAvatarFile(avatarObjectURL);
    }
  };

  const nameInputRef = useRef();

  const handleConfirm = async () => {
    const base64Avatar = state.avatarHasChanged
      ? await readStreamAsBase64(state.avatar)
      : undefined;

    const allErrors = {};
    if (!nameInputRef.current.value) {
      allErrors.name = "Name is required.";
    }
    if (!state.name && !state.avatarHasChanged) {
      allErrors.avatar = "Avatar is required.";
    }
    setErrors(allErrors);
    if (Object.keys(allErrors).length > 0) return;

    setState((s) => ({
      ...s,
      isLoading: true,
    }));

    setErrors(undefined);

    const body = {
      userId: state.userId,
      name: nameInputRef.current.value,
      facebook: socials.Facebook || "",
      instagram: socials.Instagram || "",
      tiktok: socials.TikTok || "",
      soundcloud: socials.SoundCloud || "",
      spotify: socials.Spotify || "",
      youtube: socials.YouTube || "",
      mixcloud: socials.Mixcloud || "",
      linktree: socials.Linktree || "",
      base64Avatar,
    };
    await updateProfile(body);
    userContext.setUser({
      ...userContext.user,
      ...body,
      timestamp: Date.now(),
    });
    handleClose();
  };

  useEffect(() => {
    if (!userContext.user.userId) return;
    (async () => {
      setState((s) => ({
        ...s,
        ...userContext.user,
        Facebook: userContext.user?.facebook || "",
        Instagram: userContext.user?.instagram || "",
        TikTok: userContext.user?.tiktok || "",
        SoundCloud: userContext.user?.soundcloud || "",
        Spotify: userContext.user?.spotify || "",
        YouTube: userContext.user?.youtube || "",
        Mixcloud: userContext.user?.mixcloud || "",
        Linktree: userContext.user?.linktree || "",
      }));
      setSocials({
        Facebook: userContext.user?.facebook || "",
        Instagram: userContext.user?.instagram || "",
        TikTok: userContext.user?.tiktok || "",
        SoundCloud: userContext.user?.soundcloud || "",
        Spotify: userContext.user?.spotify || "",
        YouTube: userContext.user?.youtube || "",
        Mixcloud: userContext.user?.mixcloud || "",
        Linktree: userContext.user?.linktree || "",
      });
    })();
  }, [userContext.user, isInitialDJSignup]);

  // Socials validation logic
  const filledSocials = Object.entries(socials)
    .filter(([_, value]) => value && value.trim() !== "")
    .map(([key]) => key);
  const hasRequired = filledSocials.some((social) =>
    ["Instagram", "Facebook", "TikTok"].includes(social)
  );
  const hasMinTwo = filledSocials.length >= 2;

  return state.username ? (
    <Dialog
      open={true}
      onClose={isInitialDJSignup ? () => {} : handleClose}
      PaperProps={{
        style: {
          backgroundColor: "#d8c8f4",
          color: "#4b445b",
          width: "100%",
          maxWidth: "500px",
          margin: "16px",
          maxHeight: "90vh",
          height: "auto",
          display: "flex",
          flexDirection: "column",
        },
      }}
      maxWidth={false}
    >
      <DialogTitle>
        {isInitialDJSignup ? "Become a DJ" : "DJ details"}
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          flex: "1 1 auto",
          padding: "16px 24px",
          maxHeight: "calc(90vh - 130px)",
          "&::-webkit-scrollbar": {
            width: "0.4em",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,.2)",
            borderRadius: "4px",
          },
        }}
      >
        <TextField
          autoComplete="off"
          margin="dense"
          id="name"
          label="Name"
          type="text"
          fullWidth
          variant="outlined"
          inputRef={nameInputRef}
          defaultValue={state.name}
          required
          error={!!errors?.name}
          helperText={errors?.name}
        />
        <TextField
          autoComplete="off"
          margin="dense"
          variant="outlined"
          fullWidth
          label="Avatar"
          multiline
          required
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: "div",
          }}
          error={!!errors?.avatar}
          helperText={errors?.avatar}
          inputProps={{
            children: (
              <>
                {(avatarFile || state.id) && (
                  <div
                    style={{
                      width: "100%",
                      paddingTop: "65%",
                      position: "relative",
                      marginBottom: "4px",
                    }}
                  >
                    <img
                      alt={state.name}
                      src={
                        avatarFile ||
                        (userContext.user.timestamp
                          ? `${config.cdnUrl}/avatars/${state.id}?t=${userContext.user.timestamp}`
                          : `${config.cdnUrl}/avatars/${state.id}`)
                      }
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                )}
                <div>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ marginTop: "4px" }}
                  >
                    <AddPhotoAlternate
                      sx={{ fontSize: "20px", marginRight: "4px" }}
                    />
                    {state.id ? "Change" : "Add"}
                    <input type="file" hidden onChange={handleChangeAvatar} />
                  </Button>
                </div>
              </>
            ),
          }}
        />
        <Socials socials={socials} setSocials={setSocials} />
      </DialogContent>
      <DialogActions>
        {!isInitialDJSignup && (
          <Button onClick={handleClose} variant="contained">
            Cancel
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={state.isLoading || !hasRequired || !hasMinTwo}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
}
